import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FC, Fragment } from 'react';
import { AssessmentTitle } from 'src/components/AssessmentTitle';
import { dataTestIds } from 'src/constants/data-test-ids';
import {
  SectionHeading,
  useNoteSectionTitleInCardHeader,
} from 'src/features/visits/shared/components/NoteSectionHeading';
import { formatISODateToLocaleDate } from 'src/helpers/formatDateTime';
import { ACCIDENT_TYPE_LABELS } from 'utils/lib/types/api/chart-data/chart-data.types';
import { useVisitNote } from '../../../hooks/useVisitNote';
import { AiAddedMark } from '../../scribe-recommendations/AiAddedMark';
import { findAiAddedFor, useAiAddedRecommendations } from '../../scribe-recommendations/aiAddedMarks';

export const HpiMoiContainer: FC = () => {
  const titleInCardHeader = useNoteSectionTitleInCardHeader();
  const theme = useTheme();

  const { data: note } = useVisitNote();

  // Legacy tagging: the history of present illness text is stored under the
  // chief-complaint tag.
  const historyOfPresentIllness = note?.encounterNotes.chiefComplaint?.text;
  const mechanismOfInjury = note?.encounterNotes.mechanismOfInjury?.text;
  const hpiFromAi = findAiAddedFor(useAiAddedRecommendations(), { kind: 'hpi', text: historyOfPresentIllness });
  const hpiText = <Typography sx={{ whiteSpace: 'pre-line' }}>{historyOfPresentIllness}</Typography>;

  const accident = note?.encounterNotes.accident;
  const accidentTypes = (accident?.type ?? []).map((type) => ACCIDENT_TYPE_LABELS[type] ?? type);
  const accidentDetails = [
    accident?.date ? `Date of accident: ${formatISODateToLocaleDate(accident.date) ?? accident.date}` : undefined,
    accident?.state ? `State: ${accident.state}` : undefined,
  ].filter(Boolean);

  const subSections = [
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
      data-testid={dataTestIds.progressNotePage.hpiContainer}
    >
      <AssessmentTitle>History of Present Illness</AssessmentTitle>
      {historyOfPresentIllness ? (
        hpiFromAi ? (
          <AiAddedMark recommendation={hpiFromAi}>{hpiText}</AiAddedMark>
        ) : (
          hpiText
        )
      ) : (
        <Typography color={theme.palette.text.secondary}>No history of present illness</Typography>
      )}
    </Box>,
    !!mechanismOfInjury && (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        data-testid={dataTestIds.progressNotePage.moiContainer}
      >
        <AssessmentTitle>Mechanism of Injury</AssessmentTitle>
        <Typography sx={{ whiteSpace: 'pre-line' }}>{mechanismOfInjury}</Typography>
      </Box>
    ),
    accidentTypes.length > 0 && (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        data-testid={dataTestIds.progressNotePage.accidentContainer}
      >
        <AssessmentTitle>Patient's condition related to</AssessmentTitle>
        <Typography>{accidentTypes.join(', ')}</Typography>
        {accidentDetails.length > 0 && <Typography>{accidentDetails.join(' · ')}</Typography>}
      </Box>
    ),
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      {!titleInCardHeader && <SectionHeading>HPI/MOI</SectionHeading>}
      <Stack spacing={1} sx={{ width: '100%' }}>
        {subSections.map((subSection, index) => (
          <Fragment key={index}>{subSection}</Fragment>
        ))}
      </Stack>
    </Box>
  );
};
